import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { apiClient } from '../../api/client';
import PasswordStrength from './PasswordStrength';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | null; text: string }>({
    type: null,
    text: ''
  });

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        console.error('No token found in URL parameters');
        setMessage({ type: 'error', text: 'Invalid or missing reset token. Please check your email link.' });
        setValidatingToken(false);
        return;
      }

      console.log('Validating password reset token...');

      try {
        // Use GET request with query parameter for better compatibility
        const response = await apiClient.get('/api/auth/validate-token', {
          params: { token }
        });

        console.log('Token validation response:', response.data);

        if (response.data.valid) {
          setTokenValid(true);
          console.log('Token is valid, expires at:', response.data.expires_at);
        } else {
          setMessage({ type: 'error', text: 'Invalid or expired reset token' });
        }
      } catch (error: any) {
        console.error('Token validation failed:', error);
        console.error('Error response:', error.response);

        let errorMessage = 'Invalid or expired reset token';

        if (error.response?.status === 404) {
          errorMessage = 'Reset password service not found. Please contact support.';
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }

        setMessage({
          type: 'error',
          text: errorMessage
        });
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tokenValid) {
      setMessage({ type: 'error', text: 'Invalid token. Please request a new password reset.' });
      return;
    }

    const { newPassword, confirmPassword } = formData;

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setMessage({ type: 'error', text: passwordErrors[0] });
      return;
    }

    setLoading(true);
    setMessage({ type: null, text: '' });

    try {
      const response = await apiClient.post('/api/auth/confirm-reset', {
        token,
        new_password: newPassword
      });

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Password has been reset successfully! Redirecting to login...'
        });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: { message: 'Password reset successful. Please log in with your new password.' }
          });
        }, 3000);
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Failed to reset password'
        });
      }
    } catch (error: any) {
      console.error('Password reset failed:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'An error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: 'newPassword' | 'confirmPassword', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error message when user starts typing
    if (message.type === 'error') {
      setMessage({ type: null, text: '' });
    }
  };

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-coral-50 to-white dark:from-background dark:via-background dark:to-background flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-coral-500/20 border-t-coral-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-foreground">Validating reset token...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-coral-50 to-white dark:from-background dark:via-background dark:to-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="bg-card backdrop-blur-sm border-red-500/20">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-300" />
              </div>
              <CardTitle className="text-2xl font-bold text-foreground">
                Invalid Reset Link
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-red-600 dark:text-red-300">{message.text}</p>
              <div className="space-y-2">
                <Link
                  to="/forgot-password"
                  className="block w-full bg-coral-500 hover:bg-coral-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Request New Reset Link
                </Link>
                <Link
                  to="/login"
                  className="block text-coral-700 dark:text-foreground-muted hover:text-coral-800 dark:hover:text-foreground transition-colors"
                >
                  Back to Login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-coral-50 to-white dark:from-background dark:via-background dark:to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Login Link */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-coral-700 dark:text-foreground-muted hover:text-coral-800 dark:hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>

        <Card className="bg-card backdrop-blur-sm border-coral-500/20">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-coral-500/20 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-coral-700 dark:text-foreground-muted" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">
              Reset Password
            </CardTitle>
            <p className="text-foreground-secondary mt-2">
              Enter your new password below.
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Status Message */}
            {message.type && (
              <div className={`p-4 rounded-lg flex items-start gap-3 ${
                message.type === 'success'
                  ? 'bg-green-500/10 border border-green-500/20 text-green-300'
                  : 'bg-red-500/10 border border-red-500/20 text-red-300'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <p className="text-sm">{message.text}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password Input */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-coral-700 dark:text-foreground-muted mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-coral-600 dark:text-coral-400" />
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    placeholder="Enter your new password"
                    className="w-full pl-10 pr-12 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-foreground-subtle focus:outline-none focus:ring-2 focus:ring-coral-500/40 focus:border-coral-500/40 transition-colors"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-coral-400 hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <PasswordStrength password={formData.newPassword} />
              </div>

              {/* Confirm Password Input */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-coral-700 dark:text-foreground-muted mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-coral-600 dark:text-coral-400" />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="Confirm your new password"
                    className="w-full pl-10 pr-12 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-foreground-subtle focus:outline-none focus:ring-2 focus:ring-coral-500/40 focus:border-coral-500/40 transition-colors"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-coral-400 hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !formData.newPassword || !formData.confirmPassword}
                className="w-full bg-coral-500 hover:bg-coral-600 disabled:bg-coral-500/50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-coral-500/40"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Resetting Password...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;